'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/browser';
import { googleAuthEnabled } from '@/lib/flags';
import { signInWithGoogle } from '@/lib/auth-client';

export default function ClientLogin() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Check for OAuth error messages from query params.
  // Do not show "account already exists" when user arrived via password recovery (type=recovery).
  useEffect(() => {
    if (searchParams.get('type') === 'recovery') return;

    const errorParam = searchParams.get('error');
    const methodParam = searchParams.get('method');
    const emailParam = searchParams.get('email');

    if (errorParam === 'existing_account' || (errorParam === 'existing_email' && methodParam === 'google')) {
      const errorMessage = emailParam
        ? `An account already exists with ${emailParam}. Sign in with your password, then link Google from Settings.`
        : 'An account already exists with this email. Sign in with your password, then link Google from Settings.';
      setError(errorMessage);
      if (emailParam) setEmail(emailParam);
      router.replace('/login', { scroll: false });
    }
  }, [searchParams, router]);

  // Clear stale/expired sessions on mount so login page always starts clean
  useEffect(() => {
    (async () => {
      try {
        const supabase = createClient();
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const expiry = session.expires_at ? new Date(session.expires_at * 1000) : null;
          if (!expiry || expiry < new Date()) {
            await supabase.auth.signOut();
          }
        }
      } catch {
        // Silently fail — sign-in will handle any stale state
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    setError(null);
    setShowResendOption(false);
    setResendMessage(null);
    setLoading(true);

    try {
      const supabase = createClient();
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        const msg = signInError.message;
        if (msg.includes('Email not confirmed')) {
          setError('Your email address has not been confirmed yet. Please check your inbox for a verification link.');
          setShowResendOption(true);
        } else if (msg.includes('Invalid login credentials')) {
          setError('Incorrect email or password. Please try again.');
          setShowResendOption(true);
        } else if (msg.includes('Too many requests')) {
          setError('Too many login attempts. Please wait a few minutes and try again.');
        } else {
          setError(msg);
        }
        return;
      }

      if (data.session) {
        try {
          if (remember) {
            localStorage.setItem('indiestry-remember', 'true');
          } else {
            sessionStorage.setItem('indiestry-session-only', 'true');
          }
        } catch {}

        // Best-effort last-login tracking. Fire-and-forget — failure here
        // must never block the user from reaching their destination.
        try {
          void fetch('/api/auth/track-login', {
            method: 'POST',
            credentials: 'include',
          });
        } catch {}

        // Reactivate if the account was deactivated or scheduled for deletion.
        // Awaited so the redirect query param is accurate; errors swallowed.
        let reactivatedFrom: 'deactivation' | 'deletion' | null = null;
        try {
          const res = await fetch('/api/account/reactivate', {
            method: 'POST',
            credentials: 'include',
          });
          if (res.ok) {
            const json = await res.json().catch(() => ({}));
            if (json?.reactivated) reactivatedFrom = json.from;
          }
        } catch {}

        // Onboarding gate: incomplete (and not skipped) users go to /onboarding.
        // Unverified emails are also routed there so the verification gate renders.
        let onboardingPending = false;
        try {
          const { data: prof } = await supabase
            .from('profiles')
            .select('onboarding_completed_at, onboarding_step')
            .eq('id', data.session.user.id)
            .maybeSingle();
          const dbStep = prof?.onboarding_step ?? 0;
          const dbCompleted =
            !!prof?.onboarding_completed_at || dbStep >= 5;
          const skipped = dbStep === -1;
          const emailUnverified = !data.session.user.email_confirmed_at;
          onboardingPending = emailUnverified || (!dbCompleted && !skipped);
        } catch {
          // Fail open — let the user reach /home if the check breaks.
        }

        const rawRedirect = searchParams.get('redirect') || '/home';
        // Validate redirect to prevent open redirect attacks — must be a relative path
        const redirect = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/home';
        const baseRedirect = onboardingPending ? '/onboarding' : redirect;
        const finalRedirect = reactivatedFrom && !onboardingPending
          ? `${baseRedirect}${baseRedirect.includes('?') ? '&' : '?'}reactivated=${reactivatedFrom}`
          : baseRedirect;
        router.push(finalRedirect);
        router.refresh();
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;
    setResendLoading(true);
    setResendMessage(null);
    try {
      const supabase = createClient();
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: trimmedEmail,
      });
      if (resendError) {
        setResendMessage('Could not resend. Please try again.');
      } else {
        setResendMessage('Confirmation email sent. Check your inbox.');
      }
    } catch {
      setResendMessage('Could not resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      if (!googleAuthEnabled) {
        setError('Google sign-in is disabled. Please use email and password.');
        return;
      }
      const { error } = await signInWithGoogle();
      if (error) throw error;
      // OAuth flow will redirect to /auth/callback
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(err?.message || 'An error occurred during Google sign-in. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const rawMessage = searchParams.get('message');
  const successMessage = rawMessage
    ? (() => { try { return decodeURIComponent(rawMessage); } catch { return rawMessage; } })()
    : null;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* Left panel — brand side */}
      <div className="hidden lg:flex flex-col justify-between p-12
        bg-surface-1 relative overflow-hidden">

        {/* Background decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full
          bg-accent-soft blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full
          bg-accent-soft blur-3xl translate-y-1/2 -translate-x-1/2" />

        {/* Logo */}
        <div>
          <Image src="/indiestrylogofull.png" alt="Indiestry" height={40} width={140}
            className="h-10 w-auto" />
        </div>

        {/* Center content */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-text-primary leading-tight mb-4">
            Where voice actors<br />find their next role
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed">
            Connect with casting directors, submit auditions, and build
            your professional voice acting career.
          </p>
        </div>

        {/* Honest value / invite block — no fake stats or testimonials. */}
        <div className="relative z-10 bg-surface-2 border border-border-subtle rounded-2xl p-6">
          <h3 className="text-text-primary text-base font-bold mb-2">
            Built for voice actors — and only voice actors.
          </h3>
          <p className="text-text-secondary text-sm leading-relaxed mb-4">
            A real community, not an algorithm. No ads, no recruiters, no noise.
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-4 text-sm">
            {['Profiles', 'Community feed', 'Groups', 'Job & casting board', 'Workshops'].map((f, i) => (
              <span key={f} className="inline-flex items-center gap-2">
                {i > 0 && <span className="text-border-strong" aria-hidden>·</span>}
                <span className="text-text-primary font-medium">{f}</span>
              </span>
            ))}
          </div>
          <p className="text-text-tertiary text-sm">
            <span className="text-accent font-medium">We&apos;re just getting started</span> — be one of the first.
          </p>
        </div>
      </div>

      {/* Right panel — form side */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16
        bg-surface-0 min-h-screen lg:min-h-0">

        {/* Mobile logo */}
        <div className="lg:hidden mb-8">
          <Image src="/indiestrylogofull.png" alt="Indiestry" height={40} width={140}
            className="h-10 w-auto" />
        </div>

        <div className="max-w-sm w-full mx-auto lg:mx-0">
          <h1 className="text-2xl font-bold text-text-primary mb-1">Welcome back</h1>
          <p className="text-text-secondary text-sm mb-8">Sign in to your Indiestry account</p>

          {successMessage && (
            <div className="mb-4 p-3 rounded-xl bg-surface-2 border border-border-subtle text-text-secondary text-sm">
              {successMessage}
            </div>
          )}

          {/* Google OAuth button */}
          {googleAuthEnabled && (
            <>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5
                  rounded-xl border border-border-default bg-surface-2 text-text-primary text-sm
                  font-medium hover:bg-surface-3 transition-colors mb-4 disabled:opacity-50"
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"/>
                    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
                  </svg>
                )}
                {googleLoading ? 'Signing in...' : 'Continue with Google'}
              </button>
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-subtle" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-surface-0 px-3 text-xs text-text-tertiary">or continue with email</span>
                </div>
              </div>
            </>
          )}

          {/* Email form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-text-secondary mb-1.5">
                Email address
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-surface-3 border border-border-default rounded-xl px-4 py-3
                  text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent
                  transition-colors text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="login-password" className="text-sm font-medium text-text-secondary">
                  Password
                </label>
                <a href="/forgot-password" className="text-xs text-accent hover:text-accent-hover">
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-surface-3 border border-border-default rounded-xl px-4 py-3
                    text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent
                    transition-colors text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary
                    hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                className="rounded border-border-default bg-surface-3 accent-accent cursor-pointer flex-shrink-0"
                style={{ width: '16px', height: '16px', minWidth: '16px' }}
              />
              <label htmlFor="remember-me" className="text-sm text-text-secondary cursor-pointer select-none">
                Stay signed in after closing the browser
              </label>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-surface-2 border border-border-default text-status-busy rounded-xl px-4 py-3 text-sm">
                {error}
                {showResendOption && (
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendLoading}
                    className="block mt-1 text-accent hover:text-accent-hover underline text-xs disabled:opacity-50"
                  >
                    {resendLoading ? 'Sending…' : 'Resend confirmation email'}
                  </button>
                )}
                {resendMessage && (
                  <p className="text-status-online text-xs mt-1">{resendMessage}</p>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-accent
                text-text-on-accent font-medium text-sm hover:bg-accent-hover
                disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm text-text-tertiary mt-6">
            Don&apos;t have an account?{' '}
            <a href="/signup" className="text-accent hover:text-accent-hover font-medium">
              Create one free
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
